/**
 * Word document helpers — read context, insert/replace content.
 *
 * All functions assume Office.js is loaded and the host is Word.
 */

export interface WordContext {
  selection: string;
  /** Truncated full document body (kept under `maxChars`). */
  body: string;
  truncated: boolean;
  charCount: number;
}

const DEFAULT_MAX_CHARS = 20_000;

export async function readWordContext(maxChars = DEFAULT_MAX_CHARS): Promise<WordContext> {
  return Word.run(async (ctx) => {
    const selRange = ctx.document.getSelection();
    const body = ctx.document.body;
    selRange.load('text');
    body.load('text');
    await ctx.sync();

    const fullText = body.text || '';
    const truncated = fullText.length > maxChars;
    return {
      selection: (selRange.text || '').trim(),
      body: truncated ? fullText.slice(0, maxChars) : fullText,
      truncated,
      charCount: fullText.length,
    };
  });
}

/** Insert markdown-rendered HTML (or plain text fallback) at the cursor. */
export async function insertAtCursor(text: string, html?: string): Promise<void> {
  return Word.run(async (ctx) => {
    const range = ctx.document.getSelection();
    if (html) {
      range.insertHtml(html, Word.InsertLocation.replace);
    } else {
      range.insertText(text, Word.InsertLocation.replace);
    }
    await ctx.sync();
  });
}

/** Replace the current selection (or insert at cursor if no selection). */
export async function replaceSelection(text: string, html?: string): Promise<void> {
  return insertAtCursor(text, html);
}

/** Append below current selection / end of document. */
export async function appendBelow(text: string, html?: string): Promise<void> {
  return Word.run(async (ctx) => {
    const range = ctx.document.getSelection();
    const target = range.getRange(Word.RangeLocation.end);
    if (html) {
      target.insertHtml(`<br/>${html}`, Word.InsertLocation.after);
    } else {
      target.insertText(`\n${text}`, Word.InsertLocation.after);
    }
    await ctx.sync();
  });
}
